import { useCallback, useEffect, useRef, useState } from 'react'

import type { Etat } from '../contrat'
import type { Retour } from '../composants/Resultat'
import { type ClientApi, ErreurApi } from './api'
import type { LecteurCatalogue } from './catalogue'
import { type CatalogueJeu, TABLE_MAX, chargerJeu, prevoir } from './jeu'
import { etatConfirme, memoriserEtat, memoriserTable, tableEnCours } from './local'

type Chargement<T> = { etat: 'attente' } | { etat: 'pret'; valeur: T } | { etat: 'erreur'; message: string }

export interface FicheOuverte {
  motId: string
  consommees?: string[]
}

function message(e: unknown): string {
  if (e instanceof ErreurApi && e.code === 'reseau') return 'Le serveur est injoignable pour le moment.'
  if (e instanceof ErreurApi && e.code === 'trop_de_requetes') return 'Trop de tentatives : réessayez dans une minute.'
  return 'Une erreur est survenue. Réessayez plus tard.'
}

/** Durée de la fusion avant la révélation, comme la démo jeu.html. */
const DUREE_FUSION_MS = 700
/** Durée du « −1 » qui s'envole d'une brique utilisée (mouvement.html). */
const DUREE_MOINS_UN_MS = 1400

function mouvementReduit(): boolean {
  return document.documentElement.dataset.mouvement === 'reduit' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
}

/** Cases de la table : chaque brique garde sa place, une case vide vaut `null`. */
export type Cases = (string | null)[]

const casesVides = (): Cases => Array.from({ length: TABLE_MAX }, () => null)

/** Vide les cases dont la brique n'a plus d'exemplaire disponible dans la réserve. */
function tableValide(cases: Cases, etat: Etat): Cases {
  const restants = new Map(etat.reserve.map((e) => [e.brique_id, e.exemplaires]))
  return casesVides().map((_, i) => {
    const b = cases[i] ?? null
    if (b === null) return null
    const n = restants.get(b) ?? 0
    restants.set(b, n - 1)
    return n > 0 ? b : null
  })
}

/** État d'une partie : catalogue, état confirmé, table et retours de fusion. */
export function usePartie(api: ClientApi, lecteur: LecteurCatalogue) {
  const [jeu, setJeu] = useState<Chargement<CatalogueJeu | null>>({ etat: 'attente' })
  const [etat, setEtat] = useState<Etat | null>(null)
  const [erreurEtat, setErreurEtat] = useState<string | null>(null)
  const [cases, setTable] = useState<Cases>(casesVides)
  /** Briques posées, dans l'ordre des cases. */
  const table = cases.filter((b): b is string => b !== null)
  const [retour, setRetour] = useState<Retour | null>(null)
  const [enAttente, setEnAttente] = useState(false)
  const [fiche, setFiche] = useState<FicheOuverte | null>(null)
  /** Secousse de la table après une tentative (design système 4.3), relancée à chaque essai. */
  const [secousse, setSecousse] = useState<{ force: 'legere' | 'forte'; cle: number } | null>(null)
  /** Fusion réussie en cours d'animation (mouvement.html, « Pop de découverte ») : les briques se rejoignent. */
  const [fusion, setFusion] = useState<number | null>(null)
  /** Briques qui viennent d'être consommées : leur « −1 » s'envole dans la réserve. */
  const [consommees, setConsommees] = useState<string[]>([])
  /** Découvertes de la session pas encore vues dans le codex : compteur Corail de la navigation. */
  const [nouvelles, setNouvelles] = useState<Set<string>>(new Set())
  const actif = useRef(true)
  const restauree = useRef(false)

  const confirmer = useCallback((nouvel: Etat) => {
    setEtat(nouvel)
    setTable((t) => tableValide(t, nouvel))
    void memoriserEtat(nouvel)
  }, [])

  const synchroniser = useCallback(async () => {
    const [catalogue, confirme] = await Promise.allSettled([chargerJeu(lecteur), api.etat()])
    if (!actif.current) return
    if (catalogue.status === 'fulfilled') setJeu({ etat: 'pret', valeur: catalogue.value })
    else setJeu({ etat: 'erreur', message: 'Le catalogue est illisible pour le moment.' })
    if (confirme.status === 'fulfilled') {
      setErreurEtat(null)
      confirmer(confirme.value)
    } else setErreurEtat(message(confirme.reason))
  }, [api, lecteur, confirmer])

  useEffect(() => {
    actif.current = true
    // La dernière projection confirmée s'affiche tout de suite, puis le serveur la remplace.
    void Promise.all([etatConfirme(), tableEnCours()]).then(([local, t]) => {
      restauree.current = true
      if (!actif.current) return
      if (local) {
        setEtat((e) => e ?? local)
        setTable((actuelle) => (actuelle.some(Boolean) ? actuelle : tableValide(t, local)))
      }
    })
    // oxlint-disable-next-line react/set-state-in-effect -- l'état change à la réponse du réseau, pas pendant l'effet.
    void synchroniser()
    return () => {
      actif.current = false
    }
  }, [synchroniser])

  useEffect(() => {
    if (restauree.current) void memoriserTable(cases)
  }, [cases])

  /** Pose une brique dans la première case libre ; table pleine, rien ne change. */
  const ajouter = useCallback((brique: string) => {
    setTable((t) => {
      const libre = t.indexOf(null)
      if (libre < 0) return t
      const suite = [...t]
      suite[libre] = brique
      return suite
    })
    setRetour(null)
    setFiche(null)
  }, [])

  /** Pose une brique dans la case `rang` : elle remplace celle qui s'y trouvait. */
  const poser = useCallback((brique: string, rang: number) => {
    setTable((t) => {
      if (rang < 0 || rang >= t.length) return t
      const suite = [...t]
      suite[rang] = brique
      return suite
    })
    setRetour(null)
    setFiche(null)
  }, [])

  /** Échange deux cases, pleines ou vides. */
  const deplacer = useCallback((depuis: number, vers: number) => {
    setTable((t) => {
      if (vers < 0 || vers >= t.length || depuis === vers) return t
      const suite = [...t]
      suite[depuis] = t[vers] ?? null
      suite[vers] = t[depuis] ?? null
      return suite
    })
    setRetour(null)
  }, [])

  /** Vide une case : l'autre brique garde sa place. */
  const retirer = useCallback((rang: number) => {
    setTable((t) => t.map((b, i) => (i === rang ? null : b)))
    setRetour(null)
  }, [])

  const vider = useCallback(() => {
    setSecousse(null)
    setTable(casesVides())
    setRetour(null)
  }, [])

  const fusionner = useCallback(async () => {
    if (jeu.etat !== 'pret' || !jeu.valeur || !etat) return
    // On ne fusionne que deux cases pleines.
    if (cases.some((b) => b === null)) return
    const catalogue = jeu.valeur
    const proposition = cases as string[]
    const decouverts = new Set(etat.decouvertes.map((d) => d.mot_id))
    setRetour({ etat: 'prevision', prevision: prevoir(proposition, catalogue, decouverts) })
    setConsommees([])
    setSecousse(null)
    setEnAttente(true)
    try {
      const reponse = await api.fusion({
        commande: crypto.randomUUID(),
        version_etat: etat.version_etat,
        version_catalogue: catalogue.version,
        briques: proposition,
      })
      const issue = reponse.issue
      if (issue.type === 'decouverte') {
        // Les briques se rejoignent, puis le mot se révèle et les « −1 » s'envolent (jeu.html, 700 ms).
        setFusion((f) => (f ?? 0) + 1)
        await new Promise((fin) => setTimeout(fin, mouvementReduit() ? 0 : DUREE_FUSION_MS))
        setFusion(null)
        confirmer(reponse.etat)
        setRetour({ etat: 'confirme', issue })
        setTable(casesVides())
        setConsommees(proposition)
        // Le « −1 » ne s'envole qu'une fois (1,3 s) : il ne doit pas rejouer quand l'écran réapparaît.
        setTimeout(() => setConsommees((c) => (c === proposition ? [] : c)), mouvementReduit() ? 0 : DUREE_MOINS_UN_MS)
        setNouvelles((n) => new Set(n).add(issue.mot_id))
        setFiche({ motId: issue.mot_id, consommees: proposition })
      } else {
        confirmer(reponse.etat)
        setRetour({ etat: 'confirme', issue })
        // Refus doux : une secousse brève, aucune perte (mouvement.html). Un mot déjà connu n'est pas refusé.
        if (issue.type !== 'connue') setSecousse((s) => ({ force: 'forte', cle: (s?.cle ?? 0) + 1 }))
      }
    } catch (e) {
      if (e instanceof ErreurApi && e.statut === 409) {
        await synchroniser()
        setRetour({ etat: 'message', texte: 'Votre partie a changé entre-temps. Elle est à jour : réessayez.' })
      } else {
        setRetour({ etat: 'message', texte: message(e) })
      }
    } finally {
      setEnAttente(false)
    }
  }, [api, jeu, etat, cases, confirmer, synchroniser])

  return {
    jeu,
    etat,
    erreurEtat,
    table,
    cases,
    retour,
    enAttente,
    fiche,
    secousse,
    fusion,
    consommees,
    nouvelles,
    carteVue: useCallback(
      (id: string) =>
        setNouvelles((n) => {
          if (!n.has(id)) return n
          const suite = new Set(n)
          suite.delete(id)
          return suite
        }),
      [],
    ),
    ajouter,
    poser,
    deplacer,
    retirer,
    vider,
    fusionner,
    signaler: useCallback((texte: string, ton?: 'hint' | 'fail') => setRetour({ etat: 'message', texte, ton }), []),
    /** Inverse l'ordre des briques posées : le geste proposé par un « presque ». */
    inverser: useCallback(() => {
      setTable((t) => [...t].reverse())
      setRetour(null)
    }, []),
    ouvrirFiche: useCallback((motId: string) => setFiche({ motId }), []),
    fermerFiche: useCallback(() => setFiche(null), []),
  }
}
